/**
 * Conditional required fields for business (organization) records.
 *
 * MIRROR of public._organization_missing_required_fields() (migration 2026-10-05-302). The database enforces the
 * rule for direct client writes; this copy only drives the form. Change both together.
 *
 * Business decisions implemented as defaults (owner to approve): BD-2 (Facility ID / Affiliate ID only for
 * facility/partner kinds, and not while the organization is a prospect) and BD-3 (email, phone and physical
 * address required on create; existing incomplete organizations stay editable).
 */

/** organization_type values whose records need a Facility ID and an Affiliate ID (lower-cased, trimmed). */
export const FACILITY_KIND_TYPES = [
  "retailer",
  "retailer_with_range",
  "range",
  "instructor",
  "promoter",
] as const;

export type RequiredOrgField =
  | "name"
  | "email"
  | "phone"
  | "address_line1"
  | "address_city"
  | "address_state"
  | "address_postal_code"
  | "facility_id"
  | "affiliate_id";

export const REQUIRED_FIELD_LABELS: Record<RequiredOrgField, string> = {
  name: "Business Name",
  email: "Email",
  phone: "Phone",
  address_line1: "Physical Address Line 1",
  address_city: "City",
  address_state: "State",
  address_postal_code: "Postal Code",
  facility_id: "Facility ID",
  affiliate_id: "Affiliate ID",
};

export interface RequiredFieldInput {
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  address_line1?: string | null;
  address_city?: string | null;
  address_state?: string | null;
  address_postal_code?: string | null;
  facility_id?: string | null;
  affiliate_id?: string | null;
  organization_type?: string | null;
  is_prospect?: boolean | null;
}

const blank = (v: string | null | undefined): boolean =>
  v === null || v === undefined || String(v).trim() === "";

export const isFacilityKind = (
  organizationType: string | null | undefined,
): boolean =>
  (FACILITY_KIND_TYPES as readonly string[]).includes(
    String(organizationType ?? "")
      .trim()
      .toLowerCase(),
  );

/** Required fields that are empty, in display order. */
export const getMissingRequiredOrgFields = (
  org: RequiredFieldInput,
): RequiredOrgField[] => {
  const missing: RequiredOrgField[] = [];
  const always: RequiredOrgField[] = [
    "name",
    "email",
    "phone",
    "address_line1",
    "address_city",
    "address_state",
    "address_postal_code",
  ];
  for (const f of always) {
    if (blank(org[f])) {
      missing.push(f);
    }
  }
  if (isFacilityKind(org.organization_type) && org.is_prospect !== true) {
    if (blank(org.facility_id)) {
      missing.push("facility_id");
    }
    if (blank(org.affiliate_id)) {
      missing.push("affiliate_id");
    }
  }
  return missing;
};

/**
 * Which required fields block this save. On create: every missing field. On edit: only fields this save would
 * newly leave empty (cleared, or newly applicable) -- fields an existing record was already missing never block an
 * unrelated save, exactly like the database rule.
 */
export const getBlockingRequiredOrgFields = (
  after: RequiredFieldInput,
  before?: RequiredFieldInput | null,
): RequiredOrgField[] => {
  const missingAfter = getMissingRequiredOrgFields(after);
  if (!before) {
    return missingAfter;
  }
  const missingBefore = new Set(getMissingRequiredOrgFields(before));
  return missingAfter.filter((f) => !missingBefore.has(f));
};

/** Turn a server refusal (ORGANIZATION_REQUIRED_FIELDS / ORGANIZATION_ID_READ_ONLY) into operator language. */
export const describeOrganizationSaveError = (message: string): string => {
  const m = message.match(
    /ORGANIZATION_REQUIRED_FIELDS:\s*(.*?):\s*([a-z0-9_,\s]+)$/i,
  );
  if (m) {
    const fields = m[2]
      .split(",")
      .map((f) => f.trim())
      .filter(Boolean)
      .map((f) => REQUIRED_FIELD_LABELS[f as RequiredOrgField] ?? f);
    return `${m[1].charAt(0).toUpperCase()}${m[1].slice(1)}: ${fields.join(", ")}.`;
  }
  if (message.includes("VENDOR_ONLY_UNLISTED")) {
    return "A vendor-only organization (no facility type) cannot have a physical address, map location or Facility ID, so it never appears on maps, the voucher locator or redemption lists. Choose a facility type to make it a Business + Vendor record, or leave the address empty.";
  }
  if (message.includes("ORGANIZATION_ID_READ_ONLY")) {
    return "The Organization ID cannot be changed once it is set.";
  }
  return message;
};
